/**
 * GraceCountdownBanner — Countdown-Band für das befristete Grace-Fenster
 * (Grace-Window, architecture-grace-window.md §5.3/§8).
 *
 * Rendert `null`, wenn `shouldShowGraceCountdown(sellerData, feature)` `false`
 * ist (Flag aus, Premium-Tier, permanent grandfathered, kein aktives Fenster
 * oder Advisor-Kontext-Aufrufer, der die Komponente gar nicht mountet — §6.2).
 * Dadurch ist die Komponente SELBST inert (§9 Null-Delta): sie kann bedenkenlos
 * im `canOpenItems===true`/`canCockpit===true`-Zweig platziert werden, ohne
 * dass ein zusätzlicher äußerer Bedingungs-Wrapper nötig wäre.
 *
 * Nudge-Telemetrie (exakt `FeatureTeaser.tsx`-Muster, §5.3): `view_promotion`
 * feuert GENAU 1× pro SICHTBAREM Mount — der Analytics-`useEffect` läuft
 * unbedingt (Rules of Hooks, kein Early-Return davor), guards intern aber auf
 * `visible`, damit ein unsichtbarer Mount (Banner nicht anzeigbar) NICHT
 * zählt. Der CTA-Klick feuert `select_promotion` und navigiert über
 * `buildUpgradeHref(promotionId)` (trägt den `?ref=`-Parameter für die
 * M-N4-Checkout-Attribution), nicht über eine rohe Route.
 *
 * Rundung/Schwellen normativ aus `config/graceWindow.ts` (§5.2/§8.3):
 *   - `remainingDays === 1` ⇒ Sonderfall „Nur noch heute kostenlos" (§8.2).
 *   - `remainingDays >= GRACE_MONTHS_DISPLAY_MIN_DAYS` (60) ⇒ Monats-Anzeige
 *     `floor(remainingDays / 30)`.
 *   - sonst ⇒ Tages-Anzeige (i18next-Pluralformen über `count`).
 *   - `remainingDays <= GRACE_URGENT_THRESHOLD_DAYS` (14) ⇒ Warn-Variante.
 *
 * A11y (§8.6): `role="status"` (nicht `alert` — nicht unterbrechend), CTA als
 * echtes `<button>` (via `common/Button`, native Fokus-Ring aus `index.css`
 * `button:focus-visible`), Text nicht allein farbcodiert (Warn-Variante trägt
 * zusätzlich ein Uhr-/Warn-Icon + eigenen Wortlaut). Kein Dismiss-Button
 * (Fenster ist bewusst dauerpräsent, §8.5).
 *
 * Jurist-Auflage M-G4-LEGAL (A1–A4, UWG §5/§5a): Wortlaut nutzt „kostenlos"
 * statt „inklusive"; `detail` benennt die Zugangsfolge explizit inkl.
 * Einstiegspreis; `reassurance` (Entwarnung „keine automatischen Kosten")
 * wird SICHTBAR unter `detail` gerendert (eigene Zeile, Teil des
 * `role="status"`-Elements) — nicht nur als aria-label/Tooltip.
 *
 * Tarif-Parametrisierung (Tester-Fund T-2, Re-Loop 1 2026-07-29): `detail` und
 * `reassurance` trugen den Tarif-Namen und den Einstiegspreis HARTKODIERT
 * („FaeBill Premium (9,99 €/Monat)" / „…bis Sie auf Premium upgraden") und
 * rendern für JEDES `feature`-Prop identisch. Für `feature="vorsteuerCockpit"`
 * — seit dem Marketing-Relaunch ein ULTRA-Feature — labelte der Banner damit
 * ein Ultra-Feature als Premium (§5 Abs. 2 Nr. 1 UWG, derselbe Fehlertyp, den
 * dieser PR beseitigt). Beide Strings tragen jetzt `{{plan}}`/`{{planName}}`;
 * welcher Tarif eingesetzt wird, leitet sich aus `promotionId` über
 * `getPromotionTargetTier` ab — DIESELBE Tabelle, aus der auch der
 * `?tier=`-Parameter der Upgrade-Hrefs kommt (ein Ort, ein Tarif).
 * `feature="openItems"`/`"dunning"` bleiben dadurch unverändert bei Premium.
 *
 * @module components/premium/GraceCountdownBanner
 */

import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { IconClock, IconWarning } from '../../icons';
import Button from '../common/Button';
import {
  getGraceStatus,
  shouldShowGraceCountdown,
  GRACE_MONTHS_DISPLAY_MIN_DAYS,
  GRACE_URGENT_THRESHOLD_DAYS,
} from '../../config/graceWindow';
import type { SellerLike, GrandfatheredFeature } from '../../config/entitlements';
import { buildUpgradeHref, getPromotionTargetTier, NudgePromotionId } from '../../config/nudgePromotions';
import { formatDate } from '../../utils/formatters';
import analyticsService from '../../services/analyticsService';

export interface GraceCountdownBannerProps {
  /** Rohes Seller-Doc (`useAuth().sellerData`) — die Komponente ruft `shouldShowGraceCountdown` selbst auf. */
  sellerData: SellerLike | null;
  feature: GrandfatheredFeature;
  /** i18n-Key + Default für den Feature-Namen im Satz („…ist {{feature}} Teil von Premium"). */
  featureNameKey: string;
  featureNameDefault: string;
  /** Nudge-Fläche (§5.5) — REQUIRED wie bei FeatureTeaser (Compiler erzwingt Vermessung). */
  promotionId: NudgePromotionId;
  creativeSlot?: string;
  /** i18n namespace(s), an `useTranslation` durchgereicht. Default `'common'`. */
  namespace?: string | string[];
}

/**
 * Countdown-Band für ein befristetes Grace-Fenster (§5.3). Siehe Modul-JSDoc
 * für Verhalten/A11y/Telemetrie-Vertrag.
 */
export function GraceCountdownBanner({
  sellerData,
  feature,
  featureNameKey,
  featureNameDefault,
  promotionId,
  creativeSlot,
  namespace = 'common',
}: GraceCountdownBannerProps) {
  const { t, i18n } = useTranslation(namespace);
  const navigate = useNavigate();

  const visible = shouldShowGraceCountdown(sellerData, feature);
  const status = getGraceStatus(sellerData, feature);

  // Telemetrie (§5.3, exakt FeatureTeaser-Muster): Hook läuft unbedingt (Rules
  // of Hooks), feuert `view_promotion` aber NUR, wenn der Banner tatsächlich
  // sichtbar ist — ein unsichtbarer Mount (z. B. Flag aus) zählt nicht.
  useEffect(() => {
    if (!visible || status.state !== 'active') {
      return;
    }
    analyticsService.trackViewPromotion({
      promotion_id: promotionId,
      ...(creativeSlot ? { creative_slot: creativeSlot } : {}),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, status.state, promotionId, creativeSlot]);

  if (!visible || status.state !== 'active') {
    return null;
  }

  const { remainingDays, expiresAtMs } = status;
  const isUrgent = remainingDays <= GRACE_URGENT_THRESHOLD_DAYS;
  const featureName = t(featureNameKey, featureNameDefault);
  const dateStr = formatDate(new Date(expiresAtMs), { locale: i18n.language });

  let headline: string;
  if (remainingDays === 1) {
    headline = t('graceBanner.lastDay', 'Nur noch heute kostenlos');
  } else if (remainingDays >= GRACE_MONTHS_DISPLAY_MIN_DAYS) {
    const months = Math.floor(remainingDays / 30);
    headline = t('graceBanner.remainingMonths', {
      count: months,
      defaultValue: 'Noch {{count}} Monate kostenlos',
    });
  } else {
    headline = t('graceBanner.remainingDays', {
      count: remainingDays,
      defaultValue: 'Noch {{count}} Tage kostenlos',
    });
  }

  // T-2: Ziel-Tarif aus DER EINEN Tabelle (`NUDGE_PROMOTION_TARGET_TIER`) statt
  // hartkodiert im Wortlaut. `free` kann als Grace-Ziel nicht vorkommen (ein
  // Grace-Fenster endet immer in einem KOSTENPFLICHTIGEN Tarif), wird aber
  // fail-safe auf die Premium-Formulierung abgebildet, statt einen leeren
  // Platzhalter in den Satz zu rendern.
  const targetTier = getPromotionTargetTier(promotionId);
  const planOffer =
    targetTier === 'ultra'
      ? t('graceBanner.planOfferUltra', 'FaeBill Ultra (19,99 €/Monat)')
      : t('graceBanner.planOfferPremium', 'FaeBill Premium (9,99 €/Monat)');
  const planName =
    targetTier === 'ultra'
      ? t('graceBanner.planNameUltra', 'Ultra')
      : t('graceBanner.planNamePremium', 'Premium');

  const detail = t('graceBanner.detail', {
    date: dateStr,
    feature: featureName,
    plan: planOffer,
    defaultValue: 'Ab dem {{date}} steht {{feature}} nur noch mit {{plan}} zur Verfügung.',
  });
  const reassurance = t('graceBanner.reassurance', {
    planName,
    defaultValue:
      'Es entstehen keine automatischen Kosten – nach Ablauf endet lediglich der Zugriff auf dieses Feature, bis Sie auf {{planName}} upgraden.',
  });

  const handleCtaClick = () => {
    analyticsService.trackSelectPromotion({
      promotion_id: promotionId,
      ...(creativeSlot ? { creative_slot: creativeSlot } : {}),
    });
    navigate(buildUpgradeHref(promotionId));
  };

  const toneClasses = isUrgent
    ? 'bg-[var(--tone-warning-bg)] border-[var(--tone-warning-border)] text-[var(--tone-warning-fg)]'
    : 'bg-[var(--tone-info-bg)] border-[var(--tone-info-border)] text-[var(--tone-info-fg)]';

  return (
    <div
      role="status"
      data-testid="grace-countdown-banner"
      className={`mb-4 p-4 border rounded-lg flex flex-col sm:flex-row sm:items-center gap-3 ${toneClasses}`}
    >
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <span aria-hidden="true" className="mt-0.5 flex-shrink-0">
          {isUrgent ? <IconWarning size={18} /> : <IconClock size={18} />}
        </span>
        <div className="min-w-0">
          <p className="text-sm font-semibold">{headline}</p>
          <p className="text-xs mt-0.5 opacity-90">{detail}</p>
          <p className="text-xs mt-0.5 opacity-75">{reassurance}</p>
        </div>
      </div>
      <Button
        type="button"
        onClick={handleCtaClick}
        size="sm"
        className="flex-shrink-0 self-start sm:self-center"
      >
        {t('graceBanner.cta', 'Jetzt upgraden')}
      </Button>
    </div>
  );
}

export default GraceCountdownBanner;
